/**
 * @file components/audit-tasks/GlobalSyncBadge.tsx
 * @description Task 詳情頁全局同步徽章。
 *
 * 顯示線上/離線狀態 + 待同步佇列數量；點擊「同步」按鈕觸發手動同步：
 * 取出 outbox 中所有 pending 條目，逐筆 fetch 重送，成功後從 outbox 刪除。
 *
 * 用於 task 詳情頁 PageShell 的 actions 區，所有 phase 共享，
 * 避免每個子面板各自實作網路狀態與待同步提示。
 *
 * 行為：
 *  - 掛載時讀一次 pending count，並監聽 window 'online'/'offline' 事件
 *  - 每 5 秒輪詢一次 pending count（防止其他元件加 outbox 後本徽章沒即時更新）
 *  - 離線或 pending 為 0 時，「同步」按鈕 disabled
 */

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Space, Typography } from 'antd';
import { message } from '@/lib/antd-app';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { countPendingOutbox } from '@/pwa/db';
import { syncOnce } from '@/pwa/sync';

export function GlobalSyncBadge() {
  // 線上狀態：以 navigator.onLine 為初始值，再透過 online/offline 事件更新
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  // 待同步條目數量（outbox 中 status='pending' 的計數）
  const [pending, setPending] = useState(0);
  // 手動同步進行中旗標（控制 Button 的 loading 狀態）
  const [syncing, setSyncing] = useState(false);

  /** 重新讀取 pending count 並更新 state；異常時記 console 不拋出。 */
  const refresh = useCallback(async () => {
    try {
      setPending(await countPendingOutbox());
    } catch (e) {
      console.error('[sync-badge] count pending failed', e);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onOnline = () => {
      setIsOnline(true);
      void refresh();
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    // 5 秒輪詢一次（防止其他元件加 outbox 後本徽章沒即時更新）
    const interval = setInterval(() => void refresh(), 5000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(interval);
    };
  }, [refresh]);

  /**
   * 手動同步：統一走 syncOnce，避免與其他同步入口並發處理同一批 outbox。
   */
  const sync = async () => {
    setSyncing(true);
    try {
      const result = await syncOnce();
      await refresh();
      if (result.failed === 0) {
        message.success(`同步完成（${result.success} 筆）`);
      } else {
        message.warning(`同步：成功 ${result.success}，失敗 ${result.failed}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Space size={8}>
      {isOnline ? (
        <Space size={4}>
          <Wifi size={14} className="text-green-500" />
          <Typography.Text style={{ color: '#16a34a', fontSize: 13 }}>線上</Typography.Text>
        </Space>
      ) : (
        <Space size={4}>
          <WifiOff size={14} className="text-red-500" />
          <Typography.Text style={{ color: '#dc2626', fontSize: 13 }}>離線</Typography.Text>
        </Space>
      )}
      {pending > 0 && <Badge count={pending} size="small" />}
      <Button
        size="small"
        icon={<RefreshCw size={12} />}
        loading={syncing}
        disabled={!isOnline || pending === 0}
        onClick={() => void sync()}
      >
        同步
      </Button>
    </Space>
  );
}
